import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ShieldAlert, X } from "lucide-react";
import { Capacitor } from "@capacitor/core";
import { useApp } from "@/context/AppContext";
import { useRollback, onProblemVersion } from "@/lib/guards";
import { getAppVersion, WEB_BUILD } from "@/lib/appVersion";

const SEEN_KEY = "yalola.rollback.seen";

// Lot 672 — repli admin actif pour cette plateforme ET le membre est sur une version plus récente que la stable
// (natif : build > build stable ; web : estampille de build différente) → bandeau discret, fermable (session).
// Lot 733 — le mode allégé local (redémarrages en boucle) n'affiche PLUS rien au membre : les fonctions coupées sont des
// conforts invisibles et le mot « sécurisé » inquiétait ; l'information reste dans Admin › Plantages (« Redémarrage inattendu ×N »).

export const RollbackBanner = () => {
  const { t } = useApp();
  const { pathname } = useLocation();
  const rb = useRollback();
  const [problem, setProblem] = useState(false);
  const [hidden, setHidden] = useState(() => { try { return sessionStorage.getItem(SEEN_KEY) === "1"; } catch { return false; } });

  useEffect(() => {
    let alive = true;
    if (!rb?.active) { setProblem(false); return undefined; }
    const platform = Capacitor.isNativePlatform() ? Capacitor.getPlatform() : "web";
    if (platform === "web") { setProblem(onProblemVersion(rb, "web", process.env.REACT_APP_WEB_BUILD || WEB_BUILD)); return undefined; }
    getAppVersion().then((v) => { if (alive) setProblem(onProblemVersion(rb, platform, v?.build)); }).catch(() => {});
    return () => { alive = false; };
  }, [rb]);

  if (!problem || hidden || pathname === "/live" || pathname.startsWith("/live/")) return null;
  const close = () => { setHidden(true); try { sessionStorage.setItem(SEEN_KEY, "1"); } catch { /* noop */ } };
  const title = t("rollback.banner_title", "Nous corrigeons un souci sur cette version");
  const hint = t("rollback.banner_hint", "Certaines fonctions sont temporairement désactivées. Merci de ta patience.");
  return (
    <div className="fixed left-0 right-0 z-[64] px-3 pointer-events-none" style={{ top: "calc(var(--sa-top, 0px) + 0.75rem)" }} data-testid="rollback-banner" data-kind="rollback">
      <div className="pointer-events-auto mx-auto max-w-md rounded-2xl shadow-lg border px-3 py-2.5 flex items-center gap-3" style={{ background: "var(--yalola-surface)", borderColor: "var(--yalola-border)" }}>
        <span className="h-9 w-9 rounded-xl shrink-0 grid place-items-center" style={{ background: "var(--yalola-surface-alt)", color: "var(--yalola-primary)" }}><ShieldAlert size={18} /></span>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold" data-testid="rollback-banner-title">{title}</div>
          <div className="text-xs" style={{ color: "var(--yalola-text-muted)" }} data-testid="rollback-banner-hint">{hint}</div>
        </div>
        <button type="button" onClick={close} className="shrink-0 h-8 min-w-8 px-1.5 grid place-items-center rounded-full" style={{ color: "var(--yalola-text-muted)" }} aria-label={t("common.close", "Fermer")} data-testid="rollback-banner-dismiss"><X size={16} /></button>
      </div>
    </div>
  );
};
